import { useState, useEffect, useRef, RefObject } from "react";

interface Size {
  width: number;
  height: number;
  contentWidth: number; // Excludes padding
  contentHeight: number; // Excludes padding
}

/**
 * Hook to get the size of an element
 * @param observePadding - Whether to observe the padding of the element
 * @param deps - Optional dependency array; when provided, the effect re-runs when deps change (e.g. when the element is conditionally mounted)
 * @returns [RefObject<T>, Size] - The ref object and the size of the element
 */

export const useElementSize = <T extends HTMLElement>(
  observePadding = false,
  /** When the element is conditionally mounted (e.g. inside Activity visible/hidden), pass a value that changes when it becomes visible so the effect re-runs and observes the new element. */
  measurementTrigger?: unknown
): [RefObject<T>, Size] => {
  const elementRef = useRef<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    contentWidth: 0,
    contentHeight: 0,
  });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const updateSize = () => {
      const computedStyle = getComputedStyle(element);

      // Padding values
      const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
      const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;

      const width = element.offsetWidth;
      const height = element.offsetHeight;

      const contentWidth = observePadding ? width - paddingLeft - paddingRight : width;
      const contentHeight = observePadding ? height - paddingTop - paddingBottom : height;

      setSize({ width, height, contentWidth, contentHeight });
    };

    const observer = new ResizeObserver(() => updateSize());
    observer.observe(element);

    // Initial size calculation
    updateSize();

    return () => {
      observer.disconnect();
    };
  }, [observePadding, measurementTrigger]);

  return [elementRef as RefObject<T>, size];
};
